import {
  DndContext,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  isThreadPinned,
  sortActiveThreads,
  sortPinnedThreads,
  threadManagementSection,
} from "@afar-research/afar";
import { scopedThreadKey, scopeThreadRef } from "@afar-research/client-runtime/environment";
import type { ScopedThreadRef } from "@afar-research/contracts";
import * as Schema from "effect/Schema";
import { memo, useCallback, useMemo, useRef, useState } from "react";

import { useLocalStorage } from "../hooks/useLocalStorage";
import { useThreadPlacement, placementKeysById } from "../hooks/useThreadPlacement";
import type { useThreadManagement } from "../hooks/useThreadManagement";
import { cn } from "../lib/utils";
import type { SidebarThreadSummary } from "../types";
import {
  applySidebarThreadDrop,
  planSidebarThreadDrop,
  resolveSidebarDropTarget,
  resolveSidebarDropVerb,
  sidebarListItemId,
  sidebarMarkerId,
  type SidebarDropVerb,
  type SidebarListItem,
  type SidebarSection,
} from "./Sidebar.logic";
import { createSidebarCollisionDetection, createSidebarSortingStrategy } from "./Sidebar.drag";
import { SidebarDragLifecycle, SidebarPointerSensor } from "./Sidebar.pointer";
import {
  SIDEBAR_DRAG_LABEL_HEIGHT,
  SidebarDragBoundary,
  SidebarSectionHeader,
  SidebarSectionPlaceholder,
  SortableThreadRow,
  type SortableThreadRowBag,
} from "./SidebarSections";

const SETTLED_SHELF_EXPANDED_KEY = "afar-research:sidebar:settled-expanded";
const SNOOZED_SHELF_EXPANDED_KEY = "afar-research:sidebar:snoozed-expanded";
// Recent history is the common lookup; the deep tail waits behind Show more.
const SETTLED_TAIL_INITIAL_COUNT = 10;
const SETTLED_TAIL_PAGE_COUNT = 25;

const EMPTY_THREADS: readonly SidebarThreadSummary[] = [];

const threadKeyOf = (thread: SidebarThreadSummary) =>
  scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id));

const threadRefOf = (thread: SidebarThreadSummary): ScopedThreadRef =>
  scopeThreadRef(thread.environmentId, thread.id);

export interface SidebarThreadSectionsProps {
  readonly threads: readonly SidebarThreadSummary[];
  readonly activeRouteThreadKey: string | null;
  readonly manageThread: ReturnType<typeof useThreadManagement>;
  /** Everything a row needs that does not vary by section. */
  readonly renderRow: (input: {
    readonly thread: SidebarThreadSummary;
    readonly pinned: boolean;
    readonly isActive: boolean;
    readonly orderedThreadKeys: readonly string[];
    readonly bag: SortableThreadRowBag;
    readonly dropVerb: SidebarDropVerb | null;
  }) => React.ReactNode;
}

/**
 * The sidebar's thread list: one flat, sortable list of rows and the markers
 * between them, so a drag can cross a section boundary and the markers decide
 * what that means.
 *
 * A thread carries only the placement it has actually been given. No order key
 * is not missing data — it sorts by creation until a drag assigns one, and the
 * planner materializes keys for the block the first time one is needed.
 */
export const SidebarThreadSections = memo(function SidebarThreadSections(
  props: SidebarThreadSectionsProps,
) {
  const { threads, activeRouteThreadKey, manageThread, renderRow } = props;
  const { pinThread, unpinThread, applyAssignments, capabilitiesOf } = useThreadPlacement();

  const [settledExpanded, setSettledExpanded] = useLocalStorage(
    SETTLED_SHELF_EXPANDED_KEY,
    false,
    Schema.Boolean,
  );
  const [snoozedExpanded, setSnoozedExpanded] = useLocalStorage(
    SNOOZED_SHELF_EXPANDED_KEY,
    false,
    Schema.Boolean,
  );
  const [settledVisibleCount, setSettledVisibleCount] = useState(SETTLED_TAIL_INITIAL_COUNT);

  // Held at its destination while the Environment applies the drop, so the row
  // does not snap back between the release and the events landing.
  const [optimisticDrop, setOptimisticDrop] = useState<{
    readonly key: string;
    readonly section: "pinned" | "active" | "settled";
    readonly occurredAt: string;
    readonly orderKey: string | undefined;
  } | null>(null);
  const [dragState, setDragState] = useState<{
    readonly activeKey: string;
    readonly activeSection: SidebarSection;
    readonly targetSection: SidebarSection | null;
  } | null>(null);

  const { pinned, active, snoozed, settled } = useMemo(() => {
    // Snooze wake times are second-precise, so this reads a real clock rather
    // than a quantized tick.
    const now = Date.now();
    const pinnedThreads: SidebarThreadSummary[] = [];
    const activeThreads: SidebarThreadSummary[] = [];
    const snoozedThreads: SidebarThreadSummary[] = [];
    const settledThreads: SidebarThreadSummary[] = [];
    for (const raw of threads) {
      if (raw.archivedAt !== null) continue;
      const thread =
        optimisticDrop?.key === threadKeyOf(raw)
          ? applySidebarThreadDrop(
              raw,
              optimisticDrop.section,
              optimisticDrop.occurredAt,
              optimisticDrop.orderKey,
            )
          : raw;
      // Snooze outranks settlement, and settlement outranks a pin: a thread
      // the user put away stays away until it wakes.
      const section = threadManagementSection(thread, now);
      if (section === "snoozed") snoozedThreads.push(thread);
      else if (section === "settled") settledThreads.push(thread);
      else if (isThreadPinned(thread)) pinnedThreads.push(thread);
      else activeThreads.push(thread);
    }
    return {
      pinned: sortPinnedThreads(pinnedThreads),
      active: sortActiveThreads(activeThreads),
      // Soonest wake first: "what comes back next" is the shelf's question.
      snoozed: snoozedThreads.toSorted(
        (left, right) =>
          Date.parse(left.management?.snoozedUntil ?? "") -
          Date.parse(right.management?.snoozedUntil ?? ""),
      ),
      settled: settledThreads.toSorted(
        (left, right) =>
          Date.parse(right.management?.settledAt ?? right.updatedAt) -
          Date.parse(left.management?.settledAt ?? left.updatedAt),
      ),
    };
  }, [optimisticDrop, threads]);

  // The open thread is never hidden behind a collapsed shelf or Show more:
  // a deep link into it must still reach its row and its way back out.
  const keepsRouteThread = useCallback(
    (list: readonly SidebarThreadSummary[], visible: readonly SidebarThreadSummary[]) => {
      if (activeRouteThreadKey === null) return visible;
      if (visible.some((thread) => threadKeyOf(thread) === activeRouteThreadKey)) return visible;
      const routeThread = list.find((thread) => threadKeyOf(thread) === activeRouteThreadKey);
      return routeThread === undefined ? visible : [...visible, routeThread];
    },
    [activeRouteThreadKey],
  );

  const visibleSettled = useMemo(
    () => keepsRouteThread(settled, settled.slice(0, settledVisibleCount)),
    [keepsRouteThread, settled, settledVisibleCount],
  );
  const renderedSettled = useMemo(
    () => (settledExpanded ? visibleSettled : keepsRouteThread(visibleSettled, EMPTY_THREADS)),
    [keepsRouteThread, settledExpanded, visibleSettled],
  );
  const renderedSnoozed = useMemo(
    () => (snoozedExpanded ? snoozed : keepsRouteThread(snoozed, EMPTY_THREADS)),
    [keepsRouteThread, snoozed, snoozedExpanded],
  );
  const hiddenSettledCount = settled.length - visibleSettled.length;

  const listItems = useMemo((): readonly SidebarListItem[] => {
    if (pinned.length + active.length + snoozed.length + settled.length === 0) return [];
    const rowsOf = (
      list: readonly SidebarThreadSummary[],
      section: SidebarSection,
    ): SidebarListItem[] =>
      list.map((thread) => ({ kind: "thread", key: threadKeyOf(thread), section }));
    const items: SidebarListItem[] = [{ kind: "marker", marker: "pinned-header" }];
    items.push(...rowsOf(pinned, "pinned"));
    items.push({ kind: "marker", marker: "pinned-divider" });
    items.push({ kind: "marker", marker: "active-placeholder" });
    items.push(...rowsOf(active, "active"));
    if (snoozed.length > 0) {
      items.push({ kind: "marker", marker: "snoozed-header" });
      items.push(...rowsOf(renderedSnoozed, "snoozed"));
    }
    items.push({ kind: "marker", marker: "settled-header" });
    items.push({ kind: "marker", marker: "settled-placeholder" });
    items.push(...rowsOf(renderedSettled, "settled"));
    return items;
  }, [active, pinned, renderedSettled, renderedSnoozed, settled.length, snoozed.length]);

  const sortableIds = useMemo(() => listItems.map(sidebarListItemId), [listItems]);
  const orderedThreadKeys = useMemo(
    () => listItems.flatMap((item) => (item.kind === "thread" ? [item.key] : [])),
    [listItems],
  );
  const orderedThreadKeysRef = useRef(orderedThreadKeys);
  orderedThreadKeysRef.current = orderedThreadKeys;

  const threadByKey = useMemo(
    () => new Map(threads.map((thread) => [threadKeyOf(thread), thread])),
    [threads],
  );
  const sectionByKey = useMemo(() => {
    const map = new Map<string, SidebarSection>();
    const add = (list: readonly SidebarThreadSummary[], section: SidebarSection) => {
      for (const thread of list) map.set(threadKeyOf(thread), section);
    };
    add(pinned, "pinned");
    add(active, "active");
    add(snoozed, "snoozed");
    add(settled, "settled");
    return map;
  }, [active, pinned, settled, snoozed]);

  // Dragging is only offered where the Environment can persist the result.
  const draggableKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const thread of threads) {
      if (capabilitiesOf(thread.environmentId).pinning) keys.add(threadKeyOf(thread));
    }
    return keys;
  }, [capabilitiesOf, threads]);

  // The sensor is owned rather than left to dnd-kit so unmounting the list
  // (search replaces it) can cancel an in-flight gesture.
  const sensorRef = useRef<SidebarPointerSensor | null>(null);
  const sensors = useSensors(
    useSensor(SidebarPointerSensor, {
      distance: 4,
      onAttach: (sensor: SidebarPointerSensor) => {
        sensorRef.current = sensor;
      },
      onFinish: () => {
        sensorRef.current = null;
      },
    }),
  );
  const collisionDetection = useMemo(
    () =>
      createSidebarCollisionDetection(
        (id) => draggableKeys.has(id) || id.startsWith("sidebar-marker-"),
        {
          items: listItems,
        },
      ),
    [draggableKeys, listItems],
  );
  const sortingStrategy = useMemo(
    () =>
      createSidebarSortingStrategy({
        items: listItems,
        settledOrder: settled.map(threadKeyOf),
        settledExpanded,
        settledVisibleCount,
        routeThreadKey: activeRouteThreadKey,
        snoozedThreadCount: renderedSnoozed.length,
        boundaryLabelHeight: SIDEBAR_DRAG_LABEL_HEIGHT,
      }),
    [
      activeRouteThreadKey,
      listItems,
      renderedSnoozed.length,
      settled,
      settledExpanded,
      settledVisibleCount,
    ],
  );

  const cancelDrag = useCallback(() => {
    sensorRef.current?.cancel();
    setDragState(null);
  }, []);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const activeKey = String(event.active.id);
      const activeSection = sectionByKey.get(activeKey);
      if (activeSection === undefined) return;
      setDragState({ activeKey, activeSection, targetSection: activeSection });
    },
    [sectionByKey],
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const target = event.over
        ? resolveSidebarDropTarget(listItems, String(event.active.id), String(event.over.id))
        : null;
      setDragState((current) =>
        current === null || current.activeKey !== String(event.active.id)
          ? current
          : { ...current, targetSection: target?.section ?? null },
      );
    },
    [listItems],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const activeKey = String(event.active.id);
      const state = dragState;
      setDragState(null);
      if (!event.over || state === null) return;
      const target = resolveSidebarDropTarget(listItems, activeKey, String(event.over.id));
      if (target === null) return;
      const thread = threadByKey.get(activeKey);
      if (thread === undefined) return;

      const plan = planSidebarThreadDrop({
        activeKey,
        activeSection: state.activeSection,
        activePinned: isThreadPinned(thread),
        activeSettled: thread.management?.settledAt != null,
        target,
        pinnedOrder: pinned.map(threadKeyOf),
        pinnedKeysById: placementKeysById(pinned, "pinned"),
        activeOrder: active.map(threadKeyOf),
        activeKeysById: placementKeysById(active, "active"),
      });
      if (plan.kind === "none") return;

      const resolveRef = (key: string) => {
        const candidate = threadByKey.get(key);
        return candidate === undefined ? null : threadRefOf(candidate);
      };
      const occurredAt = new Date().toISOString();
      const ref = threadRefOf(thread);

      void (async () => {
        switch (plan.kind) {
          case "reorder-pinned":
            setOptimisticDrop({
              key: activeKey,
              section: "pinned",
              occurredAt,
              orderKey: undefined,
            });
            await applyAssignments(plan.assignments, "pinned", resolveRef);
            break;
          case "pin":
            setOptimisticDrop({
              key: activeKey,
              section: "pinned",
              occurredAt,
              orderKey: plan.orderKey,
            });
            await pinThread(ref, plan.orderKey);
            await applyAssignments(plan.extraAssignments, "pinned", resolveRef);
            break;
          case "move-active":
            setOptimisticDrop({
              key: activeKey,
              section: "active",
              occurredAt,
              orderKey: plan.assignments.find((entry) => entry.id === activeKey)?.orderKey,
            });
            if (plan.unpin) await unpinThread(ref);
            if (plan.unsettle || plan.unsnooze) await manageThread(ref, "restore");
            await applyAssignments(plan.assignments, "active", resolveRef);
            break;
          case "settle":
            setOptimisticDrop({
              key: activeKey,
              section: "settled",
              occurredAt,
              orderKey: undefined,
            });
            await manageThread(ref, "settle");
            break;
        }
        setOptimisticDrop(null);
      })();
    },
    [
      active,
      applyAssignments,
      dragState,
      listItems,
      manageThread,
      pinThread,
      pinned,
      threadByKey,
      unpinThread,
    ],
  );

  const dropVerb =
    dragState === null
      ? null
      : resolveSidebarDropVerb(dragState.activeSection, dragState.targetSection);
  const dragging = dragState !== null;
  const isTarget = (section: SidebarSection) => dragState?.targetSection === section;

  if (listItems.length === 0) return null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={cancelDrag}
    >
      <SidebarDragLifecycle onUnmount={cancelDrag} />
      <SortableContext items={sortableIds} strategy={sortingStrategy}>
        <ul
          className="relative flex w-full min-w-0 flex-col gap-0.5"
          style={{ ["--sidebar-drag-label-height" as string]: `${SIDEBAR_DRAG_LABEL_HEIGHT}px` }}
        >
          {listItems.map((item) => {
            if (item.kind === "marker") {
              switch (item.marker) {
                case "pinned-header":
                  return (
                    <SidebarDragBoundary
                      key={sidebarMarkerId(item.marker)}
                      marker="pinned-header"
                      label="Pinned"
                      visible={dragging || pinned.length > 0}
                      isDropTarget={isTarget("pinned")}
                    />
                  );
                case "pinned-divider":
                  return (
                    <SidebarDragBoundary
                      key={sidebarMarkerId(item.marker)}
                      marker="pinned-divider"
                      label="Active"
                      visible={dragging}
                      isDropTarget={isTarget("active")}
                    />
                  );
                case "active-placeholder":
                  return (
                    <SidebarSectionPlaceholder
                      key={sidebarMarkerId(item.marker)}
                      marker="active-placeholder"
                      label="Move to Active"
                      showHint={dragging && active.length === 0}
                      isDropTarget={isTarget("active")}
                    />
                  );
                case "snoozed-header":
                  return (
                    <SidebarSectionHeader
                      key={sidebarMarkerId(item.marker)}
                      marker="snoozed-header"
                      label={`Snoozed (${snoozed.length})`}
                      dragging={dragging}
                      toggle={{
                        expanded: snoozedExpanded,
                        onToggle: () => setSnoozedExpanded((value) => !value),
                      }}
                    />
                  );
                case "settled-header":
                  return (
                    <SidebarSectionHeader
                      key={sidebarMarkerId(item.marker)}
                      marker="settled-header"
                      label={`Settled (${settled.length})`}
                      dragging={dragging}
                      isDropTarget={isTarget("settled")}
                      toggle={{
                        expanded: settledExpanded,
                        onToggle: () => setSettledExpanded((value) => !value),
                      }}
                    />
                  );
                case "settled-placeholder":
                  return (
                    <SidebarSectionPlaceholder
                      key={sidebarMarkerId(item.marker)}
                      marker="settled-placeholder"
                      label="Settle"
                      showHint={dragging && renderedSettled.length === 0}
                      isDropTarget={isTarget("settled")}
                    />
                  );
              }
            }
            const thread = threadByKey.get(item.key);
            if (thread === undefined) return null;
            return (
              <SortableThreadRow
                key={item.key}
                id={item.key}
                disabled={!draggableKeys.has(item.key)}
              >
                {(bag) => (
                  <li
                    ref={bag.setNodeRef}
                    {...bag.listeners}
                    className={cn("list-none", bag.isDragging && "z-10")}
                    style={{
                      transform: CSS.Translate.toString(bag.transform),
                      transition: bag.transition,
                    }}
                  >
                    {renderRow({
                      thread,
                      pinned: item.section === "pinned",
                      isActive: activeRouteThreadKey === item.key,
                      orderedThreadKeys: orderedThreadKeysRef.current,
                      bag,
                      dropVerb: bag.isDragging ? dropVerb : null,
                    })}
                  </li>
                )}
              </SortableThreadRow>
            );
          })}
          {settledExpanded && hiddenSettledCount > 0 ? (
            <li className="list-none px-2 py-1">
              <button
                type="button"
                className="cursor-pointer text-xs text-sidebar-muted-foreground/70 hover:text-sidebar-foreground"
                onClick={() => setSettledVisibleCount((count) => count + SETTLED_TAIL_PAGE_COUNT)}
              >
                Show {Math.min(hiddenSettledCount, SETTLED_TAIL_PAGE_COUNT)} more
              </button>
            </li>
          ) : null}
        </ul>
      </SortableContext>
    </DndContext>
  );
});
